import type { JobWithDetails } from "@/api/hooks/useJob";
import type { Attachment } from "@/api/raptor";
import { useApi, useDelete } from "@mb-pro-ui/utils";
import { Add, CameraAlt, Delete, PhotoLibrary } from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Stack,
  SwipeableDrawer,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

function WebCameraCapture({
  onCapture,
  onCancel,
  onError,
}: {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      onError("Ez az eszköz nem támogatja a kamerát.");
      onCancel();
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((mediaStream) => {
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      })
      .catch((err) => {
        onError("Nem sikerült elindítani a kamerát: " + err.message);
        onCancel();
      });

    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [onCancel, onError, stream]);

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        onCapture(blob);
      }
    }, "image/jpeg");
  };

  return (
    <div style={{ padding: 16 }}>
      <video ref={videoRef} autoPlay playsInline style={{ width: "100%" }} />
      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <button onClick={takePhoto}>Fotó készítése</button>
        <button onClick={onCancel}>Mégse</button>
      </div>
    </div>
  );
}

type PhotoCaptureProps = {
  jobId: string;
  attachments: JobWithDetails["attachments"];
  refetch: () => void;
};

export default function PhotoGalleryCapture({
  jobId,
  attachments,
  refetch,
}: PhotoCaptureProps) {
  const { formatMessage } = useIntl();
  const api = useApi();
  const theme = useTheme();

  const [showWebCamera, setShowWebCamera] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: async ({ jobId, file }: { jobId: string; file: File }) => {
      return api(`/actions/raptor/jobs/${jobId}/attach-file`, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "Content-Disposition": `attachment; filename="${encodeURIComponent(file.name)}"`,
        },
        body: file,
      });
    },
    onSuccess: () => {
      refetch();
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const deleteMutation = useDelete("raptor/attachments", {
    onSuccess: () => {
      refetch();
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file) return;

      setError(null);

      uploadMutation.mutate({ jobId, file });
    },
    [uploadMutation, jobId],
  );

  const removeAttachment = useCallback(
    (attachmentId: string) => {
      deleteMutation.mutate(attachmentId);
    },
    [deleteMutation],
  );

  const handleCameraCapture = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      e.target.value = "";
    },
    [handleFileSelect],
  );

  const handleGallerySelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      e.target.value = "";
    },
    [handleFileSelect],
  );

  const handleDrawerToggle = (isOpen: boolean) => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (isOpen && themeColorMeta) {
      setDrawerOpen(true);
      themeColorMeta.setAttribute("content", "#7e7e7e");
    } else {
      setDrawerOpen(false);
      themeColorMeta?.setAttribute("content", "#fcfcfc");
    }
  };

  const openCamera = useCallback(() => {
    handleDrawerToggle(false);
    if (/Android|iPhone|iPad/i.test(navigator.userAgent)) {
      setTimeout(() => {
        cameraInputRef.current?.click();
      }, 300);
    } else {
      setTimeout(() => {
        setShowWebCamera(true);
      }, 300);
    }
  }, []);

  const openGallery = useCallback(() => {
    handleDrawerToggle(false);
    setTimeout(() => {
      galleryInputRef.current?.click();
    }, 300);
  }, []);

  const closeError = useCallback(() => {
    setError(null);
  }, []);

  const hasAttachments = attachments?.some(
    (attachment) => attachment["attachment-type"] === "photo",
  );

  return (
    <>
      <Card>
        <CardContent sx={{ pb: "16px !important" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={hasAttachments ? 2 : 0}
          >
            <Typography variant="raptor-sub-h">
              {formatMessage({ defaultMessage: "Attach photo" })}
            </Typography>
            <IconButton
              color="primary"
              onClick={() => handleDrawerToggle(true)}
              size="small"
            >
              <Add
                sx={{
                  color: "#2587BC",
                }}
              />
            </IconButton>
          </Stack>

          {!!attachments?.length && (
            <Stack spacing={1}>
              {attachments
                .filter(
                  (attachment) => attachment["attachment-type"] === "photo",
                )
                .map((attachment) => (
                  <Box
                    key={attachment.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.action.hover, 0.04),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Avatar
                      src={attachment.url}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        mr: 2,
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                        transition: "transform 0.2s ease-in-out",
                      }}
                      onClick={() => setPreviewAttachment(attachment)}
                    />
                    {attachment.own && (
                      <IconButton
                        size="small"
                        onClick={() => removeAttachment(attachment.id)}
                        disabled={deleteMutation.isPending}
                        sx={{
                          color: "error.main",
                          ml: "auto",
                        }}
                      >
                        {deleteMutation.isPending ? (
                          <CircularProgress size={16} color="error" />
                        ) : (
                          <Delete fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                ))}
            </Stack>
          )}
          {uploadMutation.isPending && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                mt: attachments?.length ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  mr: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={24} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatMessage({
                    defaultMessage: "Uploading in progress...",
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMessage({ defaultMessage: "Please wait" })}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => handleDrawerToggle(false)}
        onOpen={() => handleDrawerToggle(true)}
        disableSwipeToOpen
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "60vh",
              background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
            },
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: "grey.300",
            borderRadius: 2,
            mx: "auto",
            mt: 1.5,
            mb: 2,
          }}
        />

        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="raptor-main-h" align="center">
            {formatMessage({ defaultMessage: "Attach photo" })}
          </Typography>
        </Box>

        <List sx={{ px: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={openCamera}
              sx={{
                borderRadius: 2,
                mb: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                  transform: "translateY(-1px)",
                },
                "&:disabled": { opacity: 0.6 },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <ListItemIcon>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 50,
                    bgcolor: "primary.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CameraAlt sx={{ color: "#FFFFFF", fontSize: 20 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="raptor-main-h" sx={{ lineHeight: 1 }}>
                    {formatMessage({ defaultMessage: "Camera" })}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatMessage({
                      defaultMessage: "Take a photo using your device camera",
                    })}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1, mx: 2 }} />

          <ListItem disablePadding>
            <ListItemButton
              onClick={openGallery}
              sx={{
                borderRadius: 2,
                "&:hover": {
                  bgcolor: "action.hover",
                  transform: "translateY(-1px)",
                },
                "&:disabled": { opacity: 0.6 },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <ListItemIcon>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "success.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhotoLibrary sx={{ color: "#FFFFFF", fontSize: 20 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="raptor-main-h" sx={{ lineHeight: 1 }}>
                    {formatMessage({ defaultMessage: "Gallery" })}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatMessage({
                      defaultMessage: "Select photo from gallery",
                    })}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Box sx={{ p: 3, pt: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleDrawerToggle(false)}
            sx={{
              py: 1,
            }}
          >
            <Typography
              variant="raptor-button-text"
              sx={{ color: (theme) => theme.palette.primary.main }}
            >
              {formatMessage({ defaultMessage: "Cancel" })}
            </Typography>
          </Button>
        </Box>
      </SwipeableDrawer>

      <Dialog
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        {previewAttachment && (
          <>
            <DialogContent sx={{ p: 0 }}>
              <img
                src={previewAttachment.url}
                alt="img"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
              <Stack
                direction="row"
                spacing={1}
                width={"100%"}
                justifyContent="space-between"
              >
                <Button onClick={() => setPreviewAttachment(null)}>
                  {formatMessage({ defaultMessage: "Close" })}
                </Button>
                {previewAttachment.own && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                      removeAttachment(previewAttachment.id);
                      setPreviewAttachment(null);
                    }}
                    disabled={deleteMutation.isPending}
                  >
                    {formatMessage({ defaultMessage: "Delete" })}
                  </Button>
                )}
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      <input
        ref={cameraInputRef}
        type="file"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleCameraCapture}
      />

      <input
        ref={galleryInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleGallerySelect}
      />

      {showWebCamera && (
        <Dialog
          open
          onClose={() => setShowWebCamera(false)}
          fullWidth
          maxWidth="sm"
        >
          <WebCameraCapture
            onCapture={(blob) => {
              const file = new File([blob], `webcam-${Date.now()}.jpg`, {
                type: "image/jpeg",
              });
              handleFileSelect(file);
              setShowWebCamera(false);
            }}
            onCancel={() => setShowWebCamera(false)}
            onError={(message) => setError(message)}
          />
        </Dialog>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={closeError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
            },
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
